import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Card, TableContainer, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn, SortOrder } from '../../../components/GRTable/GRTable'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { LanguageMapsUpdate, useGetLanguageMapsQuery, useUpdateLanguageMapsMutation } from '../../api/language'
import { InternalListToolbar } from '../../components/InternalListToolbar/InternalListToolbar'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../components/RowActions/RowActions'
import { MetadataTagEditDialog } from '../../features/metadata-tags/components/MetadataTagEditDialog/MetadataTagEditDialog'
import {
  buildMetadataTagRows,
  filterMetadataTagRows,
  getActiveMetadataTagIds,
  hasMetadataTagNames,
  MetadataTagRow,
  toMetadataTagDeletion,
  toMetadataTagUpdate,
} from '../../features/metadata-tags/helpers/metadataTagHelpers'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'
import { ContentLanguageTranslation } from '../../types/InternalAnalystComment'

type CustomProps = {
  onEdit: (row: MetadataTagRow) => void
  onDelete: (row: MetadataTagRow) => void
}

type DialogState = { open: boolean; metadataTag?: MetadataTagRow }

export const MetadataTagsPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-metadata-tags'))
  const { t } = useTranslation()
  const { run } = useMutationFeedback()
  const languageMapsQuery = useGetLanguageMapsQuery()
  const { refetch: refetchLanguageMaps } = languageMapsQuery
  const [updateLanguageMaps, { isLoading: isSaving }] = useUpdateLanguageMapsMutation()
  const [search, setSearch] = useState('')
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<MetadataTagRow>>()

  const allRows = useMemo(() => buildMetadataTagRows(languageMapsQuery.data), [languageMapsQuery.data])
  const rows = useMemo(() => filterMetadataTagRows(allRows, search), [allRows, search])
  const activeIds = useMemo(() => getActiveMetadataTagIds(allRows), [allRows])
  // A new id is only checked against the stored ids, so writing before they load could overwrite one.
  const isLoaded = languageMapsQuery.isSuccess && !!languageMapsQuery.data

  const tableColumns: GRTableColumn<MetadataTagRow, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-service-management:metadata_tags_id'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.id}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.id.toLowerCase(),
        // GRTable compares strings in reverse, so DESC lists ids A to Z.
        sortOrder: SortOrder.DESC,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-service-management:metadata_tags_name_en'),
        accessor: ({ row }) => row.names.en,
        sortable: true,
        sortAccessor: ({ row }) => row.names.en?.toLowerCase() ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 180 } },
      },
      {
        labelAccessor: t('internal-service-management:metadata_tags_name_ja'),
        accessor: ({ row }) => row.names.ja,
        sortable: true,
        sortAccessor: ({ row }) => row.names.ja ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 160 } },
      },
      {
        labelAccessor: t('internal-service-management:metadata_tags_name_zh'),
        accessor: ({ row }) => row.names.zh,
        sortable: true,
        sortAccessor: ({ row }) => row.names.zh ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 120 } },
      },
      rowActionsColumn<MetadataTagRow, CustomProps>({
        actions: ({ row, customTableProps }) => [
          { type: 'edit', label: t('internal-service-management:metadata_tags_edit'), onClick: () => customTableProps?.onEdit(row) },
          { type: 'delete', label: t('internal-service-management:metadata_tags_delete'), onClick: () => customTableProps?.onDelete(row) },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (row) => setDialogState({ open: true, metadataTag: row }),
      onDelete: (row) =>
        setConfirmDialogData({
          title: t('internal-service-management:metadata_tags_delete'),
          confirmText: t('internal-service-management:metadata_tags_delete_confirm', { id: row.id }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: row,
        }),
    }),
    [t]
  )

  // The backend has answered writes with success without storing them, so every write is checked against fresh maps.
  const writeAndConfirm = useCallback(
    async (id: string, names: ContentLanguageTranslation, update: LanguageMapsUpdate) => {
      await updateLanguageMaps(update).unwrap()
      const latest = await refetchLanguageMaps().unwrap()
      if (!hasMetadataTagNames(latest, id, names)) {
        throw new Error(`Metadata tag ${id} was not stored`)
      }
    },
    [refetchLanguageMaps, updateLanguageMaps]
  )

  const handleSave = useCallback(
    async (metadataTag: MetadataTagRow) => {
      if (!isLoaded) return
      const saved = await run(writeAndConfirm(metadataTag.id, metadataTag.names, toMetadataTagUpdate(metadataTag.id, metadataTag.names)), {
        success: t('internal-service-management:metadata_tags_saved', { id: metadataTag.id }),
        error: t('internal-service-management:metadata_tags_save_failed'),
      })
      if (saved.ok) setDialogState({ open: false })
    },
    [isLoaded, run, t, writeAndConfirm]
  )

  const handleConfirmDelete = useCallback(
    async (confirmed?: ConfirmDialogData<MetadataTagRow>) => {
      setConfirmDialogData(undefined)
      const row = confirmed?.data
      if (!row) return

      await run(writeAndConfirm(row.id, {}, toMetadataTagDeletion(row.id)), {
        success: t('internal-service-management:metadata_tags_deleted', { id: row.id }),
        error: t('internal-service-management:metadata_tags_delete_failed'),
      })
    },
    [run, t, writeAndConfirm]
  )

  return (
    <Box>
      <InternalListToolbar
        search={{ value: search, onChange: setSearch, placeholder: t('internal-service-management:metadata_tags_search') }}
        add={{ label: t('internal-service-management:metadata_tags_add'), onClick: () => setDialogState({ open: true }), disabled: !isLoaded }}
      />

      <QueryStateBoundary
        isLoading={languageMapsQuery.isLoading}
        isError={languageMapsQuery.isError}
        errorText={t('internal-service-management:metadata_tags_load_failed')}
      >
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey={(row) => row.id}
            customProps={customProps}
            noRowsLabel={t('internal-common:no_rows')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <MetadataTagEditDialog
        open={dialogState.open}
        metadataTag={dialogState.metadataTag}
        activeIds={activeIds}
        isSaving={isSaving || languageMapsQuery.isFetching || !isLoaded}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirmDelete} />
    </Box>
  )
}
